// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./components/Header";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./components/LandingPage";
import { ActiveEditorProvider } from "./context/ActiveEditorContext";
import EntityTypeFormDesigner from "./components/EntityTypeFormDesigner";
import FormLayoutBuilder from "./components/FormLayoutBuilder";
import GenericViewDesigner from "./components/template-system/GenericViewDesigner";
// import ThemeDesigner from "./components/ThemeDesigner";
import { useParams } from "react-router-dom";
import { Container, Alert, CircularProgress } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Loading from "./components/Loading";
import IndustryAnalysisDashboard from "./components/industry-analysis/IndustryAnalysisDashboard";
import { SalesProcessProvider } from "./components/sales-process/SalesProcessProvider";
import {
  clearAuthState,
  isAuthenticated as checkAuth,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import AuthGuard from "./components/AuthGuard";

// Lazy-loaded components
const Dashboard = lazy(() => import("./components/Dashboard"));
const Startup = lazy(() => import("./components/Startup"));
const Home = lazy(() => import("./components/Home"));
const ThemeDesigner = lazy(() => import("./components/ThemeDesigner"));
const PageBuilder = lazy(() => import("./components/page-builder/PageBuilder"));
const TemplateBuilder = lazy(() =>
  import("./components/template-builder/TemplateBuilder")
);
const CoffeeShopAboutPage = lazy(() =>
  import("./components/template-system/CoffeeShopAboutPage")
);
const MenuBuilder = lazy(() => import("./components/MenuBuilder"));
const AgentConfigForm = lazy(() => import("./components/AgentConfigForm"));
const WorkflowDesignSummary = lazy(() =>
  import("./components/WorkflowDesignSummary")
);
const SubscriptionForm = lazy(() => import("./components/SubscriptionForm"));
const APIKeyRegistration = lazy(() =>
  import("./components/APIKeyRegistration")
);
const ApiCallRegistration = lazy(() =>
  import("./components/ApiCallRegistration")
);
const Scheduler = lazy(() => import("./components/Scheduler"));
const WorkflowCanvas = lazy(() => import("./components/WorkflowCanvas"));
const SalesProcessDashboard = lazy(() =>
  import("./components/sales-process/SalesProcessDashboard")
);
const SalesProcessScreen = lazy(() =>
  import("./components/sales-process/SalesProcessScreen")
);
const AIAutomationDashboardBuilder = lazy(() =>
  import(
    "./components/ai-automation-dashboard-builder/AIAutomationDashboardBuilder"
  )
);
const AgentAutomationDashboard = lazy(() =>
  import("./components/AgentAutomationDashboard")
);
const RAGDocumentSubmission = lazy(() =>
  import("./components/RAGDocumentSubmission")
);
const RAGQueryInterface = lazy(() => import("./components/RAGQueryInterface"));
const RAGTestQueries = lazy(() => import("./components/RAGTestQueries"));

// Sample landing page data for demonstration
const sampleLandingPageData = {
  hero: {
    title: "Welcome to Our Business",
    subtitle: "Innovative Solutions for Modern Challenges",
    image: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg",
    cta: "Get Started",
  },
  features: [
    {
      title: "Feature 1",
      description: "Description of feature 1",
      icon: "FaStar",
      image:
        "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg",
    },
    {
      title: "Feature 2",
      description: "Description of feature 2",
      icon: "FaStar",
      image:
        "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg",
    },
    {
      title: "Feature 3",
      description: "Description of feature 3",
      icon: "FaStar",
      image:
        "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg",
    },
  ],
  about: {
    title: "About Us",
    description:
      "We are a company dedicated to providing innovative solutions.",
    image: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg",
  },
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState(themes.cyan); // Default theme is cyan

  useEffect(() => {
    console.log("App.js - Checking authentication state");

    // Start AWS redirect prevention
    const cleanup = preventAWSRedirects();

    // Clear all AWS state on app start
    clearAllAWSState();

    const userIsAuthenticated = checkAuth();
    console.log("App.js - User is authenticated:", userIsAuthenticated);

    if (userIsAuthenticated) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      // Clear any potential AWS Cognito state that might interfere
      clearAuthState();
    }

    const savedTheme = localStorage.getItem("THEME");
    if (savedTheme) {
      try {
        const themeData = JSON.parse(savedTheme);
        setTheme(themeData);
      } catch (e) {
        console.error("Error parsing saved theme:", e);
        setTheme(themes.cyan);
      }
    }

    // Return cleanup function
    return cleanup;
  }, []);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <SalesProcessProvider>
            <DndProvider backend={HTML5Backend}>
              <CssBaseline />
              <AuthGuard>
                <Header
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                />
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {/* Public routes - accessible without authentication */}
                    <Route
                      path="/login"
                      element={
                        <AuthForm setIsAuthenticated={setIsAuthenticated} />
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <AuthForm setIsAuthenticated={setIsAuthenticated} />
                      }
                    />
                    <Route path="/confirm-signup" element={<ConfirmSignUp />} />

                    {/* All other routes - require authentication or redirect to login */}
                    {isAuthenticated ? (
                      <>
                        {/* Protected routes - require authentication */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/theme-designer"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <ThemeDesigner
                                onChangeTheme={handleThemeChange}
                              />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/coffee-about"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <CoffeeShopAboutPage
                                onChangeTheme={handleThemeChange}
                              />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/startup"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <Startup />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/page-builder"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <PageBuilder />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/template-builder"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <TemplateBuilder />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-automation-dashboard"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <AgentAutomationDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ai-automation-dashboard-builder"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <AIAutomationDashboardBuilder />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/rag-document-submission"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <RAGDocumentSubmission />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/rag-query-interface"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <RAGQueryInterface />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/rag-test-queries"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <RAGTestQueries />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/menu-builder"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <MenuBuilder />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/agent-config"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <AgentConfigForm />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/workflow-design-summary"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <WorkflowDesignSummary />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/subscription"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <SubscriptionForm />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/api-key-registration"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <APIKeyRegistration />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/api-call-registration"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <ApiCallRegistration />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/scheduler"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <Scheduler />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/workflow-designer"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <WorkflowCanvas />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/sales-process"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <SalesProcessDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/sales-process/:screenId"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <SalesProcessScreen />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/industry-analysis/:startupName"
                          element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                              <IndustryAnalysisDashboard />
                            </ProtectedRoute>
                          }
                        />
                      </>
                    ) : (
                      /* If not authenticated, redirect ALL routes to login */
                      <Route
                        path="*"
                        element={<Navigate to="/login" replace />}
                      />
                    )}
                  </Routes>
                </Suspense>
              </AuthGuard>
            </DndProvider>
          </SalesProcessProvider>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
